<script>
//Repositories
import Repositories from '@/repositories';
const OrdersRepository = Repositories.get('orders');

//Mixins
import textMixin from '@/mixins/textMixin';
import dateMixin from '@/mixins/dateMixin';
import statusMixin from '@/mixins/statusMixin';

//Components
import GridComponent from '@/components/widgets/grids/api-linked-grid';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import StatusComponent from '@/components/widgets/badges/status';

//Modals
import ModalLayout from '@/router/layouts/modal-view.vue';
import ReceptionInfoModal from '../../management/main.vue';

export default {
  props: {
    filters: Object,
    fields: Array,
  },
  components: {
    GridComponent,
    Loading,
    StatusComponent,
    ModalLayout,
    ReceptionInfoModal,
  },
  mixins: [textMixin, dateMixin, statusMixin],
  data() {
    return {
      currentDate: new Date(),
      receptionsList: [],
      fullListSize: 0,
      isLoading: false,
      receptionForModal: null,
      lastClickedReception: null,
    };
  },
  created() {
    this.currentDate.setHours(0, 0, 0, 0);

    this.loadData();
  },
  methods: {
    loadData() {
      this.isLoading = true;

      OrdersRepository.customREST
        .getReceptions({ ...this.filters })
        .then(({ data }) => {
          if (data != null && data.data != null) {
            this.receptionsList = data.data;
            this.fullListSize = data.count;
          }
        })
        .catch(() => {
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        })
        .finally(() => (this.isLoading = false));
    },
    receptionUpdated(reception) {
      let foundIndex = this.receptionsList.findIndex(
        (x) => x.idPublic == reception.idPublic
      );

      this.receptionsList = [
        ...this.receptionsList.slice(0, foundIndex),
        reception,
        ...this.receptionsList.slice(foundIndex + 1),
      ];

      this.receptionForModal = reception;
    },
    openModal(reception, modalName) {
      this.receptionForModal = reception;
      this.lastClickedReception = modalName + '-' + reception.idPublic;
      this.$bvModal.show(modalName);
    },
    modalClosed() {
      this.receptionForModal = null;
      this.loadData();
    },
  },
  computed: {
    receptionsListComputed() {
      return this.receptionsList.map((x) => {
        let status = this.getStatusFromCode(x.status, 'Receptions');
        return { ...x, ...status };
      });
    },
    groupByReceptionDate() {
      let newArray = this.receptionsListComputed.reduce((acc, reception) => {
        let receptionDate = reception.receptionDate;
        let group = acc.find((group) => group.receptionDate === receptionDate);

        if (!group) {
          group = {
            receptionDate: receptionDate,
            receptionDateDate: new Date(receptionDate),
            receptions: [],
          };
          acc.push(group);
          acc.sort(
            (a, b) => new Date(a.receptionDate) - new Date(b.receptionDate)
          );
        }
        group.receptions.push(reception);

        return acc;
      }, []);

      return newArray;
    },
  },
  watch: {
    filters() {
      this.receptionsList = [];
      this.loadData();
    },
  },
};
</script>

<template>
  <b-card no-body class="p-3 custom-inventory-shadow">
    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      color="#f1b44c"
      :width="70"
      :height="70"
      loader="dots"
      :is-full-page="false"
    >
    </loading>

    <div v-for="item in groupByReceptionDate" :key="item.idPublic">
      <GridComponent
        id="receptions-grid"
        :fields="fields"
        :items="item.receptions"
        :borderless="true"
        headVariant="white"
      >
        <template #head(receptionDate)>
          <div
            v-if="
              item.receptionDate && new Date(item.receptionDate) < currentDate
            "
            style="min-width: 180px"
            class="d-flex flex-row text-danger"
          >
            {{ formatDate(item.receptionDate) }}
            <i class="fas fa-exclamation-triangle text-danger m-auto" />
          </div>
          <div
            v-else-if="
              Math.floor(currentDate.getTime() / 1000) ==
              Math.floor(item.receptionDateDate.getTime() / 1000)
            "
            class="text-success"
            style="min-width: 180px"
          >
            {{ formatDate(item.receptionDate) }}
          </div>
          <div v-else style="min-width: 180px">
            {{ formatDate(item.receptionDate) }}
          </div>
        </template>
        <template #cell(receptionDate)> </template>

        <template #cell(externalNumber)="data">
          <div
            style="min-width: 150px"
            v-html="
              highlightMatches(
                data.item.externalNumber == null
                  ? ''
                  : data.item.externalNumber,
                filters != null ? filters.search : ''
              )
            "
          >
          </div>
        </template>
        <template #cell(number)="data">
          <span
            v-html="
              highlightMatches(
                data.item.number,
                filters != null ? filters.search : ''
              )
            "
          ></span>
        </template>

        <template #cell(statusOrder)="data">
          <div>
            <StatusComponent
              :text="data.item.statusName"
              :variant="data.item.statusVariant"
            />
          </div>
        </template>

        <template #cell(totalItems)="data">
          {{ data.item.doneItems }} /
          {{ data.item.totalItems }}
        </template>
        <template #cell(supplierName)="data">
          <div style="min-width: 180px">
            {{ data.item.supplierName }}
          </div>
        </template>
        <template #cell(received)="data">
          <div
            @click.capture.prevent="
              openModal(data.item, 'reception-info-modal')
            "
          >
            <b-link
              :to="{
                name: 'manageReception',
                params: { idPublic: data.item.idPublic },
              }"
            >
              <i
                v-if="
                  lastClickedReception ===
                  'reception-info-modal-' + data.item.idPublic
                "
                class="fas fa-sign-in-alt pl-2 pr-2"
                style="cursor: pointer; font-size: 1.3em; color: #20976e"
              ></i>
              <i
                v-else
                class="fas fa-sign-in-alt text-success pl-2 pr-2"
                style="cursor: pointer; font-size: 1.3em"
              ></i>
            </b-link>
          </div>
        </template>
      </GridComponent>
    </div>

    <ModalLayout
      name="reception-info-modal"
      @modal-closed="modalClosed"
      :title="
        $t('receptions.reception') +
        ' ' +
        (receptionForModal != null ? receptionForModal.number : '')
      "
      :route="{
        name: 'manageReception',
        params: {
          idPublic: receptionForModal != null ? receptionForModal.idPublic : '',
        },
      }"
    >
      <div v-if="receptionForModal != null">
        <ReceptionInfoModal
          :receptionIdPublic="receptionForModal.idPublic"
          @reception-updated="receptionUpdated"
        />
      </div>
    </ModalLayout>
  </b-card>
</template>

<style scoped>
a,
a:hover,
a:focus,
a:active {
  text-decoration: none;
  color: inherit;
}
</style>